import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  clientService,
  ClientFilters,
  CreateClientRequest,
  UpdateClientRequest,
  BulkUpdateRequest,
  ImportCsvRequest,
} from '@services/client.service'

export function useClients(filters: ClientFilters = {}) {
  return useQuery({
    queryKey: ['clients', filters],
    queryFn: () => clientService.fetchClients(filters),
  })
}

export function useClient(id: number) {
  return useQuery({
    queryKey: ['client', id],
    queryFn: () => clientService.getClient(id),
    enabled: !!id,
  })
}

export function useClientStats() {
  return useQuery({
    queryKey: ['clients', 'stats'],
    queryFn: clientService.getStats,
    refetchInterval: 60000, // Refresh every minute
  })
}

export function useClientTags() {
  return useQuery({
    queryKey: ['clients', 'tags'],
    queryFn: clientService.getAllTags,
    staleTime: 300000, // Cache for 5 minutes
  })
}

export function useCreateClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateClientRequest) => clientService.createClient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['clients', 'stats'] })
    },
  })
}

export function useUpdateClient(id: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateClientRequest) => clientService.updateClient(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['client', id] })
      queryClient.invalidateQueries({ queryKey: ['clients', 'stats'] })
    },
  })
}

export function useDeleteClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => clientService.deleteClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['clients', 'stats'] })
    },
  })
}

export function useBulkDeleteClients() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (clientIds: number[]) => clientService.bulkDelete(clientIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['clients', 'stats'] })
    },
  })
}

export function useBulkUpdateClients() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: BulkUpdateRequest) => clientService.bulkUpdate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['clients', 'stats'] })
    },
  })
}

export function useImportCsv() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ImportCsvRequest) => clientService.importCsv(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['clients', 'stats'] })
      queryClient.invalidateQueries({ queryKey: ['clients', 'tags'] })
    },
  })
}

export function useExportCsv() {
  return useMutation({
    mutationFn: (filters: ClientFilters = {}) => clientService.exportCsv(filters),
  })
}
